import React from 'react';
import T from 'prop-types';
// components
import { FormattedMessage } from 'react-intl';
import ActivityActionsBar from '../ActivityActionsBar/ActivityActionsBar';
import ActivityCheckupBox from '../ActivityCheckupBox/ActivityCheckupBox';
import ActivityBox from '../ActivityBox';
import ActivityBodyCollapse from '../ActivityBodyCollapse';
import ActivityHeader from '../ActivityHeader';
import ActivityHeaderBadge from '../ActivityHeader/ActivityHeaderBadge';
import ActivityAuthorLine from '../ActivityAuthorLine/ActivityAuthorLine';
import ActivityMentions from '../ActivityMentions';
import CollapsesBox from 'components/CollapsesBox/CollapsesBox';
import FarmFeedRowComments from 'components/FarmFeed/FarmFeedRowComments';
import FarmFeedRowAddComment from 'components/FarmFeed/FarmFeedRowAddComment';
import GroupInfoCollapse from 'components/Groups/GroupInfoCollapse';
import LastCommentInfo from '../LastCommentInfo/LastCommentInfo';
import UserAvatar from 'components/UserAvatar/UserAvatar';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// assets
import agentIcon from '../everypig-agent-icon.png';
// styles
import styles from './AgentActivity.module.scss';

const cn = classnames.bind(styles);

// Activity fields that make an EveryPig Agent row re-render (used by withActivityController in the wrappers)
export const affectRerenderKeys = ['id', 'updated_at', 'is_viewed', 'flagged', 'comments', 'additional_data'];

// Shared FarmFeed layout for EveryPig Agent automated workflow events.
// A variety wrapper supplies the i18n ids; farmName and groupName are always available in the text values.
const AgentActivity = ({
  className,
  activity,
  currentUser,
  isChildActivity,
  markAsSeen,
  openDropdown,
  setActivityFlag,
  isMinimized,
  isTruncated,
  toggleMinimized,
  onViewFullActivity,
  defaultDropdownOptions,
  titleId,
  textId,
  textValues,
}) => {
  const { id, pig_group, created_at, flagged, is_viewed, mentions } = activity;
  const additionalData = activity.additional_data || {};
  const farmName = activity.farm?.name || additionalData.farm_name || '';
  const groupName = pig_group?.name || additionalData.group_name || '';
  const date = moment(created_at).format('YYYY-MM-DD');
  const isAdmin = currentUser.roles_map.admin;

  return (
    <ActivityBox
      className={className}
      isFlagged={flagged}
      isTruncated={isTruncated}
      hasMarker={!isChildActivity}
      isUnseen={!is_viewed}
      markAsSeen={markAsSeen}
      onViewFullActivity={onViewFullActivity}
    >
      {!isMinimized && <LastCommentInfo activity={activity} currentUser={currentUser} />}
      <ActivityHeader
        activityId={id}
        onOpenDropdown={(e) => openDropdown(e, defaultDropdownOptions)}
        onDoubleClick={toggleMinimized}
        isColumnDirection
      >
        <ActivityAuthorLine
          activity={activity}
          currentUser={currentUser}
          minimized={isMinimized}
          avatarSlot={<UserAvatar url={agentIcon} name="EveryPig Agent" size={40} />}
        >
          <FormattedMessage
            tagName="div"
            id="component.agentActivity.author"
            values={{
              name: activity.user?.full_name,
              b: (msg) => <b>{msg}</b>,
            }}
          />
        </ActivityAuthorLine>
        <ActivityHeaderBadge isCentered className={cn('agent-badge')}>
          <FormattedMessage id={titleId} tagName="b" />
        </ActivityHeaderBadge>
      </ActivityHeader>
      <ActivityBodyCollapse isClosed={isMinimized}>
        <div className={cn('warning-text')}>
          <FormattedMessage
            tagName="div"
            id={textId}
            values={{ b: (text) => <b>{text}</b>, farmName, groupName, ...textValues }}
          />
          <ActivityMentions isAdmin={isAdmin} users={mentions} />
        </div>

        <ActivityCheckupBox
          isAdmin={isAdmin}
          activity={activity}
          className={cn({ 'mb-15': isChildActivity })}
        >
          {pig_group && (
            <CollapsesBox>
              <GroupInfoCollapse
                isAdmin={isAdmin}
                titleClassName={cn('warning-group-info')}
                group={pig_group}
                customEndpoint={`/pig_groups/${pig_group.id}/history_dc_info?history_date=${date}`}
              />
            </CollapsesBox>
          )}
        </ActivityCheckupBox>

        {!isChildActivity && (
          <>
            <ActivityActionsBar
              activity={activity}
              currentUser={currentUser}
              openDropdown={openDropdown}
              setActivityFlag={setActivityFlag}
            />
            <FarmFeedRowComments activity={activity} />
            <FarmFeedRowAddComment activity={activity} />
          </>
        )}
      </ActivityBodyCollapse>
    </ActivityBox>
  );
};

AgentActivity.propTypes = {
  className: T.string,
  activity: T.object.isRequired,
  currentUser: T.object.isRequired,
  markAsSeen: T.func,
  isChildActivity: T.bool,
  openDropdown: T.func.isRequired,
  setActivityFlag: T.func.isRequired,
  isTruncated: T.bool.isRequired,
  isMinimized: T.bool.isRequired,
  toggleMinimized: T.func.isRequired,
  onViewFullActivity: T.func.isRequired,
  defaultDropdownOptions: T.array.isRequired,
  titleId: T.string.isRequired,
  textId: T.string.isRequired,
  textValues: T.object,
};

AgentActivity.defaultProps = {
  isChildActivity: false,
  textValues: {},
};

export default AgentActivity;
